import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  getRouteMap,
  getRouteTraffic,
  getPublicRouteTraffic,
  getRouteWeather,
  getPublicRouteWeather,
} from "@/services/routeService";
import { buildCameraPopupHtml } from "@/utils/cameraPopup";

const MPS_TO_MPH = 2.23694;

/** Enrich a merged route+traffic object with averageSpeedMph derived from the server's speedMetersPerSecond. */
const addAverageSpeed = (data) => {
  if (data?.speedMetersPerSecond) {
    data.averageSpeedMph = data.speedMetersPerSecond * MPS_TO_MPH;
  }
  return data;
};

/**
 * Custom routing hook that replaces Google Directions API while keeping Google Maps autocomplete
 */
const useCustomRouting = () => {
  const [map, setMap] = useState(null);
  const [isDraggable, setIsDraggable] = useState(false);
  const [startLocation, setStartLocation] = useState(null);
  const [endLocation, setEndLocation] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [waypoints, setWaypoints] = useState([]);
  const [activeRoute, setActiveRoute] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  // User-facing message when route planning fails (e.g. out-of-coverage 400)
  const [routeError, setRouteError] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [lastRouteResponse, setLastRouteResponse] = useState(null);
  const [trafficLinks, setTrafficLinks] = useState([]);

  // Use refs instead of state for markers to prevent render cycles
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const locationMarkersVisibleRef = useRef(true);
  const waypointMarkersRef = useRef([]);
  const userLocationMarkerRef = useRef(null);
  const polylineRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const routeChangedListenersRef = useRef([]);

  const mapRef = useRef(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const startAutocompleteRef = useRef(null);
  const endAutocompleteRef = useRef(null);
  const isInitializedRef = useRef(false);
  const calculateRouteRef = useRef(null);
  const displayRouteRef = useRef(null);
  const editPointMarkersRef = useRef([]);
  const eventMarkersRef = useRef([]);
  const eventInfoWindowRef = useRef(null);
  const cameraMarkersRef = useRef([]);
  const cameraInfoWindowRef = useRef(null);
  const startLocationRef = useRef(null);
  const endLocationRef = useRef(null);
  const skipAutoCalculateRef = useRef(false);

  /**
   * Get user's current location
   */
  const getCurrentLocation = useCallback(() => {
    if (!map) return;

    setIsLocating(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // Center map on user's location
          map.setCenter(pos);
          map.setZoom(14);

          // Clear old user location marker if exists
          if (userLocationMarkerRef.current) {
            userLocationMarkerRef.current.setMap(null);
          }

          // Create a marker at the user's location
          userLocationMarkerRef.current = new window.google.maps.Marker({
            position: pos,
            map: map,
            animation: window.google.maps.Animation.DROP,
            title: "Your Location",
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });

          setIsLocating(false);
        },
        (error) => {
          console.error("Error getting current location:", error);
          alert(
            "Unable to get your location. Please check your browser permissions.",
          );
          setIsLocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    } else {
      alert("Your browser does not support geolocation.");
      setIsLocating(false);
    }
  }, [map]);

  /**
   * Calculate bounds from points
   */
  const calculateBounds = useCallback((points) => {
    if (!points || points.length === 0)
      return {
        northeast: { lat: 0, lng: 0 },
        southwest: { lat: 0, lng: 0 },
      };

    let north = -90;
    let south = 90;
    let east = -180;
    let west = 180;

    points.forEach((point) => {
      if (point.lat > north) north = point.lat;
      if (point.lat < south) south = point.lat;
      if (point.lng > east) east = point.lng;
      if (point.lng < west) west = point.lng;
    });

    return {
      northeast: { lat: north, lng: east },
      southwest: { lat: south, lng: west },
    };
  }, []);

  /**
   * Create route objects that mimic the Google Directions API response
   */
  const createRouteObjects = useCallback(
    (path, apiData = null) => {
      if (!path || path.length < 2) return [];

      // Use server-provided metrics when available, fall back to client-side estimation
      let totalDistance = apiData?.lengthMeters || 0;
      let totalDuration = apiData?.travelTimeSeconds || 0;
      const steps = [];

      for (let i = 0; i < path.length - 1; i++) {
        const start = path[i];
        const end = path[i + 1];

        // Calculate distance between points (in meters)
        const segmentDistance = calculateDistance(
          start.lat,
          start.lng,
          end.lat,
          end.lng,
        );

        // Only accumulate client-side estimates if server didn't provide values
        if (!apiData?.lengthMeters) totalDistance += segmentDistance;

        // Estimate duration based on distance (assuming average speed of 50 km/h)
        const segmentDuration = (segmentDistance / 1000 / 50) * 3600; // in seconds
        if (!apiData?.travelTimeSeconds) totalDuration += segmentDuration;

        // Create a step for each segment
        steps.push({
          instructions: `Continue to next point`,
          distance: {
            value: segmentDistance,
            text: formatDistance(segmentDistance),
          },
          duration: {
            value: segmentDuration,
            text: formatDuration(segmentDuration),
          },
          start_location: start,
          end_location: end,
        });
      }

      // Calculate bounds
      const bounds = calculateBounds(path);

      // Create a route object that mimics the Google Directions API response
      const route = {
        legs: [
          {
            distance: {
              value: totalDistance,
              text: formatDistance(totalDistance),
            },
            duration: {
              value: totalDuration,
              text: formatDuration(totalDuration),
            },
            start_address: start,
            end_address: end,
            start_location: path[0],
            end_location: path[path.length - 1],
            steps: steps,
          },
        ],
        overview_path: path,
        bounds: new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(
            bounds.southwest.lat,
            bounds.southwest.lng,
          ),
          new window.google.maps.LatLng(
            bounds.northeast.lat,
            bounds.northeast.lng,
          ),
        ),
        warnings: [],
        waypoint_order: [],
      };

      return [route]; // Return as array to mimic multiple routes
    },
    [calculateBounds, start, end],
  );

  const calculateRoute = useCallback(() => calculateRouteRef.current(), []);
  const displayRoute = useCallback((path) => displayRouteRef.current(path), []);

  /**
   * Display route on map using polyline
   */
  displayRouteRef.current = (path) => {
    if (!map || !path || path.length === 0) return;

    // Clear existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }

    // Clear any existing edit point markers
    if (editPointMarkersRef.current && editPointMarkersRef.current.length > 0) {
      editPointMarkersRef.current.forEach((marker) => marker.setMap(null));
    }
    editPointMarkersRef.current = [];

    // Convert path to Google Maps LatLng objects
    const pathCoordinates = path.map((point) => ({
      lat: typeof point.lat === "function" ? point.lat() : point.lat,
      lng: typeof point.lng === "function" ? point.lng() : point.lng,
    }));

    // Create new polyline for the route - Do NOT make it editable
    polylineRef.current = new window.google.maps.Polyline({
      path: pathCoordinates,
      geodesic: true,
      strokeColor: "#4285F4",
      strokeOpacity: 1.0,
      strokeWeight: 5,
      draggable: false, // We'll handle this differently
      editable: false, // This prevents all the dots
    });

    polylineRef.current.setMap(map);

    // If route is draggable, add interactivity to the polyline
    if (isDraggable && polylineRef.current) {
      // Make the polyline thicker and change cursor on hover
      polylineRef.current.setOptions({
        strokeWeight: 8,
        strokeOpacity: 0.8,
        clickable: true,
        draggable: false, // We'll handle dragging manually
        editable: false,
      });

      // Add mouseover listener to show potential waypoint
      let hoverMarker = null;

      polylineRef.current.addListener("mouseover", (e) => {
        polylineRef.current.setOptions({
          strokeWeight: 10,
          strokeOpacity: 1.0,
        });
      });

      polylineRef.current.addListener("mouseout", (e) => {
        polylineRef.current.setOptions({
          strokeWeight: 8,
          strokeOpacity: 0.8,
        });

        // Remove hover marker if exists
        if (hoverMarker) {
          hoverMarker.setMap(null);
          hoverMarker = null;
        }
      });

      // Add click listener to add waypoints
      polylineRef.current.addListener("click", (e) => {
        const clickLocation = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };

        // Find the closest point on the route
        let closestPoint = null;
        let minDistance = Infinity;

        for (let i = 0; i < pathCoordinates.length - 1; i++) {
          const start = pathCoordinates[i];
          const end = pathCoordinates[i + 1];

          // Calculate distance from click to line segment
          const distance = distToSegment(
            clickLocation.lat,
            clickLocation.lng,
            start.lat,
            start.lng,
            end.lat,
            end.lng,
          );

          if (distance < minDistance) {
            minDistance = distance;

            // Find the closest point on the segment
            const t = Math.max(
              0,
              Math.min(
                1,
                ((clickLocation.lat - start.lat) * (end.lat - start.lat) +
                  (clickLocation.lng - start.lng) * (end.lng - start.lng)) /
                  ((end.lat - start.lat) * (end.lat - start.lat) +
                    (end.lng - start.lng) * (end.lng - start.lng)),
              ),
            );

            closestPoint = {
              lat: start.lat + t * (end.lat - start.lat),
              lng: start.lng + t * (end.lng - start.lng),
            };
          }
        }

        if (closestPoint) {
          // Add waypoint at the closest point on the route
          addWaypoint(closestPoint);
        }
      });
    }

    // Set markers
    setLocationMarkers(
      { lat: pathCoordinates[0].lat, lng: pathCoordinates[0].lng },
      {
        lat: pathCoordinates[pathCoordinates.length - 1].lat,
        lng: pathCoordinates[pathCoordinates.length - 1].lng,
      },
    );

    // Re-create waypoint markers if they don't exist (e.g., after route recalculation)
    // Only show them in customization mode so they don't obscure traffic/incident data
    if (
      isDraggable &&
      waypoints.length > 0 &&
      waypointMarkersRef.current.length === 0
    ) {
      waypoints.forEach((waypoint, index) => {
        const waypointMarker = new window.google.maps.Marker({
          position: waypoint.location,
          map: map,
          title: `Waypoint ${index + 1}`,
          label: `${index + 1}`,
          draggable: true,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FF5722",
            fillOpacity: 0.8,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
          },
        });

        // Add the same event listeners as in addWaypoint
        waypointMarker.addListener("dragend", () => {
          const newPosition = waypointMarker.getPosition();
          const updatedLocation = {
            lat: newPosition.lat(),
            lng: newPosition.lng(),
          };

          setWaypoints((prev) => {
            const markerIndex =
              waypointMarkersRef.current.indexOf(waypointMarker);
            if (markerIndex !== -1) {
              const updatedWaypoints = [...prev];
              updatedWaypoints[markerIndex] = {
                ...updatedWaypoints[markerIndex],
                location: updatedLocation,
              };
              setTimeout(() => calculateRoute(), 100);
              return updatedWaypoints;
            }
            return prev;
          });
        });

        waypointMarker.addListener("click", () => {
          const markerIndex =
            waypointMarkersRef.current.indexOf(waypointMarker);
          removeWaypoint(markerIndex);
        });

        waypointMarker.addListener("rightclick", () => {
          const markerIndex =
            waypointMarkersRef.current.indexOf(waypointMarker);
          removeWaypoint(markerIndex);
        });

        waypointMarker.addListener("mouseover", () => {
          waypointMarker.setIcon({
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#FF5722",
            fillOpacity: 1.0,
            strokeColor: "#FFFFFF",
            strokeWeight: 3,
          });
        });

        waypointMarker.addListener("mouseout", () => {
          waypointMarker.setIcon({
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FF5722",
            fillOpacity: 0.8,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
          });
        });

        waypointMarkersRef.current.push(waypointMarker);
      });
    }

    // Fit bounds to show the entire route
    const bounds = new window.google.maps.LatLngBounds();
    pathCoordinates.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
  };

  useEffect(() => {
    // When draggable state changes, redisplay the route to update edit points
    if (routes.length > 0 && selectedRouteIndex < routes.length) {
      const route = routes[selectedRouteIndex];
      if (route.overview_path) {
        displayRoute(route.overview_path);
      }
    }
  }, [isDraggable, routes, selectedRouteIndex, displayRoute]);

  // Initialize Google Maps and autocomplete
  useEffect(() => {
    // Avoid re-initialization
    if (isInitializedRef.current || !window.google) return;
    isInitializedRef.current = true;

    // Detect dark mode for map styling
    const isDarkMode = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const mapId = isDarkMode
      ? process.env.REACT_APP_MAP_DARK
      : process.env.REACT_APP_MAP_LIGHT;

    // Initialize map
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: 41.8781, lng: -87.6298 }, // Chicago coordinates as default
      zoom: 10,
      mapTypeControl: true,
      streetViewControl: true,
      streetViewControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      fullscreenControl: true,
      ...(mapId ? { mapId } : {}),
    });

    // Set map state
    setMap(mapInstance);

    // Add custom "Current Location" control
    const locationControlDiv = document.createElement("div");

    // Set CSS for the control
    locationControlDiv.style.backgroundColor = "#fff";
    locationControlDiv.style.border = "2px solid #fff";
    locationControlDiv.style.borderRadius = "3px";
    locationControlDiv.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    locationControlDiv.style.cursor = "pointer";
    locationControlDiv.style.marginRight = "10px";
    locationControlDiv.style.marginBottom = "10px";
    locationControlDiv.style.textAlign = "center";
    locationControlDiv.title = "Click to use your current location";

    // Set CSS for the control interior
    const controlUI = document.createElement("div");
    controlUI.style.color = "rgb(25,25,25)";
    controlUI.style.fontFamily = "Roboto,Arial,sans-serif";
    controlUI.style.fontSize = "16px";
    controlUI.style.lineHeight = "38px";
    controlUI.style.paddingLeft = "5px";
    controlUI.style.paddingRight = "5px";

    // Create location icon using SVG
    controlUI.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 38px; width: 38px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </div>
      `;

    locationControlDiv.appendChild(controlUI);

    // Setup the click event listener — use mapInstance directly because
    // getCurrentLocation captures `map` state which is still null during init.
    locationControlDiv.addEventListener("click", () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            mapInstance.setCenter(pos);
            mapInstance.setZoom(14);
            if (userLocationMarkerRef.current) {
              userLocationMarkerRef.current.setMap(null);
            }
            userLocationMarkerRef.current = new window.google.maps.Marker({
              position: pos,
              map: mapInstance,
              animation: window.google.maps.Animation.DROP,
              title: "Your Location",
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#4285F4",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 2,
              },
            });
          },
          (error) => {
            console.error("Error getting current location:", error);
            alert(
              "Unable to get your location. Please check your browser permissions.",
            );
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
        );
      } else {
        alert("Your browser does not support geolocation.");
      }
    });

    // Add the control to the map (bottom right corner)
    mapInstance.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
      locationControlDiv,
    );

    // Initialize Autocomplete for start input
    if (startInputRef.current) {
      const startAutocomplete = new window.google.maps.places.Autocomplete(
        startInputRef.current,
        { types: ["geocode", "establishment"] },
      );

      startAutocomplete.bindTo("bounds", mapInstance);
      startAutocompleteRef.current = startAutocomplete;

      // Add listener to capture selected place coordinates
      startAutocomplete.addListener("place_changed", () => {
        const place = startAutocomplete.getPlace();
        console.log("Start place selected:", place);

        if (place && place.geometry && place.geometry.location) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };

          // Update location state
          setStartLocation(location);

          // Update the input text value with the formatted address
          if (place.formatted_address) {
            setStart(place.formatted_address);
          }

          // If end not yet set, center on start
          if (!endLocationRef.current) {
            mapInstance.setCenter(location);
            mapInstance.setZoom(13);
          }
        }
      });
    }

    // Initialize Autocomplete for end input
    if (endInputRef.current) {
      const endAutocomplete = new window.google.maps.places.Autocomplete(
        endInputRef.current,
        { types: ["geocode", "establishment"] },
      );

      endAutocomplete.bindTo("bounds", mapInstance);
      endAutocompleteRef.current = endAutocomplete;

      // Add listener to capture selected place coordinates
      endAutocomplete.addListener("place_changed", () => {
        const place = endAutocomplete.getPlace();
        console.log("End place selected:", place);

        if (place && place.geometry && place.geometry.location) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };

          // Update location state
          setEndLocation(location);

          // Update the input text value with the formatted address
          if (place.formatted_address) {
            setEnd(place.formatted_address);
          }

          // If start not yet set, center on end
          if (!startLocationRef.current) {
            mapInstance.setCenter(location);
            mapInstance.setZoom(13);
          }
        }
      });
    }

    // Initialize directionsRenderer reference
    if (!directionsRendererRef.current) {
      directionsRendererRef.current = {
        _directions: null,
        _routeIndex: 0,
        setMap: (map) => {},
        setRouteIndex: (index) => {
          directionsRendererRef.current._routeIndex = index;
          setSelectedRouteIndex(index);
          if (
            directionsRendererRef.current._directions &&
            directionsRendererRef.current._directions.routes &&
            directionsRendererRef.current._directions.routes.length > index
          ) {
            displayRoute(
              directionsRendererRef.current._directions.routes[index]
                .overview_path,
            );
          }
        },
        getRouteIndex: () => directionsRendererRef.current._routeIndex,
        getDirections: () => directionsRendererRef.current._directions,
        setDirections: (response) => {
          if (response && response.routes) {
            directionsRendererRef.current._directions = response;
            directionsRendererRef.current._routeIndex = 0;
            setRoutes(response.routes);
            setSelectedRouteIndex(0);
            if (response.routes.length > 0) {
              displayRoute(response.routes[0].overview_path);
            }
          }
        },
        setOptions: (options) => {
          if (options.draggable !== undefined) {
            setIsDraggable(options.draggable);
          }
        },
        addListener: (event, handler) => {
          const listener = {
            remove: () => {
              // Remove the listener from our custom listener array
              routeChangedListenersRef.current =
                routeChangedListenersRef.current.filter((l) => l !== handler);
            },
          };

          // If it's a directions_changed event, store the handler
          if (event === "directions_changed" && typeof handler === "function") {
            routeChangedListenersRef.current.push(handler);
          }

          return listener;
        },
      };
    }
  }, [
    getCurrentLocation,
    endLocation,
    startLocation,
    routes,
    selectedRouteIndex,
    calculateRoute,
    displayRoute,
  ]);

  /**
   * Calculate route using our backend route service
   */
  calculateRouteRef.current = async () => {
    if (!startLocation || !endLocation) {
      return null;
    }

    setIsCalculating(true);
    setRouteError(null);

    try {
      // Format waypoints for API - the backend expects an array of LatLng objects
      const formattedWaypoints = waypoints.map((waypoint) => {
        // Make sure we have the location in the expected format
        const location = waypoint.location || waypoint;
        return {
          lat: location.lat,
          lng: location.lng,
        };
      });

      console.log("Calculating route with waypoints:", formattedWaypoints);

      // Call our backend routing service with proper waypoint handling
      const requestBody = {
        start: {
          latLng: {
            lat: startLocation.lat,
            lng: startLocation.lng,
          },
          address: start,
        },
        end: {
          latLng: {
            lat: endLocation.lat,
            lng: endLocation.lng,
          },
          address: end,
        },
        // The backend expects waypoints as an array of LatLng objects
        waypoints: formattedWaypoints.length > 0 ? formattedWaypoints : null,
        // These are the RouteOptions that get mapped to the @JsonUnwrapped options field
        travelMode: "DRIVING",
        metric: "DYNAMIC_TRAVEL_TIME",
        avoidHighways: false,
        avoidTolls: false,
        avoidFerries: false,
      };

      console.log("API Request body:", requestBody);

      // Step 1: Plan route (path + routeData only, no traffic)
      const response = await axios.post(
        "/lmiga/routePlanner.json",
        requestBody,
      );

      console.log("API Response:", response.data);

      const apiData = response.data?.data;
      if (!apiData || !apiData.path || apiData.path.length === 0) {
        console.error(
          "No path data returned from routing service:",
          response.data,
        );
        setRouteError("Unable to calculate a route between these locations.");
        setIsCalculating(false);
        return null;
      }

      const path = apiData.path;
      console.log("Route path returned:", path.length, "points");

      // Display the route polyline immediately (before traffic loads)
      displayRoute(path);
      setActiveRoute(path);

      // Create initial route objects with planning data only
      const routesData = createRouteObjects(path, apiData);
      setRoutes(routesData);
      setSelectedRouteIndex(0);

      // Step 2: Fetch traffic + weather data in parallel
      const [trafficResult, weatherResult] = await Promise.allSettled([
        getPublicRouteTraffic(apiData.routeData),
        getPublicRouteWeather(apiData.routeData),
      ]);

      let merged = { ...apiData };

      if (trafficResult.status === "fulfilled") {
        const trafficData = trafficResult.value?.data;
        merged = addAverageSpeed({ ...merged, ...trafficData });
        setTrafficLinks(trafficData?.links || []);
      } else {
        console.warn(
          "Traffic fetch failed, showing route without traffic:",
          trafficResult.reason,
        );
        setTrafficLinks([]);
      }

      if (weatherResult.status === "fulfilled") {
        const weatherData = weatherResult.value?.data;
        merged.weatherAlerts = weatherData?.weatherAlerts || [];
        merged.winterConditions = weatherData?.winterConditions || [];
      } else {
        console.warn("Weather fetch failed:", weatherResult.reason);
      }

      setLastRouteResponse(merged);
      const updatedRoutes = createRouteObjects(path, merged);
      setRoutes(updatedRoutes);

      // Call any route changed listeners
      routeChangedListenersRef.current.forEach((listener) => {
        if (typeof listener === "function") {
          listener();
        }
      });

      setIsCalculating(false);
      return routesData;
    } catch (error) {
      console.error("Error calculating route:", error);
      // Log more detailed error information
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
      } else if (error.request) {
        console.error("Error request:", error.request);
      } else {
        console.error("Error message:", error.message);
      }
      // Surface the server's message (e.g. OUT_OF_COVERAGE from gateway#15)
      setRouteError(
        error.response?.data?.error?.message ||
          "Unable to calculate a route between these locations.",
      );
      setTrafficLinks([]);
      setIsCalculating(false);
      return null;
    }
  };

  /**
   * Alternative implementation using Google's DirectionsService
   * If your backend routing API isn't working properly, you could use this instead
   */
  const calculateRouteWithGoogle = async () => {
    if (!startLocation || !endLocation) {
      return null;
    }

    setIsCalculating(true);

    try {
      // Convert waypoints to Google's format
      const googleWaypoints = waypoints.map((waypoint) => ({
        location: new window.google.maps.LatLng(
          waypoint.location.lat,
          waypoint.location.lng,
        ),
        stopover: true,
      }));

      // Create a promise to wrap the Google DirectionsService
      const directionsPromise = new Promise((resolve, reject) => {
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
          {
            origin: new window.google.maps.LatLng(
              startLocation.lat,
              startLocation.lng,
            ),
            destination: new window.google.maps.LatLng(
              endLocation.lat,
              endLocation.lng,
            ),
            waypoints: googleWaypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === "OK") {
              resolve(response);
            } else {
              reject(new Error(`Directions request failed: ${status}`));
            }
          },
        );
      });

      // Wait for the directions result
      const result = await directionsPromise;

      // Process the route result
      if (result && result.routes && result.routes.length > 0) {
        const route = result.routes[0];

        // Extract path points from the route overview path
        const path = route.overview_path.map((point) => ({
          lat: point.lat(),
          lng: point.lng(),
        }));

        // Create route objects that mimic our expected format
        const routesData = createRouteObjects(path);
        setRoutes(routesData);
        setSelectedRouteIndex(0);

        // Display the first route
        displayRoute(path);

        // Save the active route path for waypoint proximity checks
        setActiveRoute(path);

        setIsCalculating(false);
        return routesData;
      } else {
        console.error("No routes found");
        setIsCalculating(false);
        return null;
      }
    } catch (error) {
      console.error("Error calculating route with Google:", error);
      setIsCalculating(false);
      return null;
    }
  };

  // Replace your calculateRouteRef.current with this function,
  // or use it as an alternative if your backend API isn't working

  /**
   * Calculate distance between two points using Haversine formula
   */
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // in meters
  };

  /**
   * Format distance in a human-readable way
   */
  const formatDistance = (distance) => {
    if (distance < 1000) {
      return `${Math.round(distance)} m`;
    } else {
      return `${(distance / 1000).toFixed(1)} km`;
    }
  };

  /**
   * Format duration in a human-readable way
   */
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    } else {
      return `${minutes} min`;
    }
  };

  /**
   * Capture the active route path for waypoint placement
   */
  const captureActiveRoutePath = useCallback(() => {
    if (routes.length > 0 && selectedRouteIndex < routes.length) {
      const route = routes[selectedRouteIndex];
      if (route.overview_path) {
        setActiveRoute(route.overview_path);
        return route.overview_path;
      }
    }
    return null;
  }, [routes, selectedRouteIndex]);

  /**
   * Handle route changes when dragged
   */
  const handleRouteChanged = useCallback(() => {
    if (!polylineRef.current) return [];

    const path = polylineRef.current.getPath();
    const newPath = [];

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      newPath.push({
        lat: point.lat(),
        lng: point.lng(),
      });
    }

    // Update the active route
    setActiveRoute(newPath);

    // Create new route objects based on the updated path
    const updatedRoutes = createRouteObjects(newPath);
    setRoutes(updatedRoutes);

    // Call any route changed listeners
    routeChangedListenersRef.current.forEach((listener) => {
      if (typeof listener === "function") {
        listener();
      }
    });

    return updatedRoutes;
  }, [createRouteObjects]);

  /**
   * Set markers for start and end locations
   */
  const setLocationMarkers = useCallback(
    (newStartLocation, newEndLocation) => {
      if (!map) return;

      // Clear existing markers
      if (startMarkerRef.current) {
        startMarkerRef.current.setMap(null);
        startMarkerRef.current = null;
      }

      if (endMarkerRef.current) {
        endMarkerRef.current.setMap(null);
        endMarkerRef.current = null;
      }

      const pinLabel = (text) => ({
        text,
        color: "#FFFFFF",
        fontWeight: "bold",
      });

      const pinIcon = {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
        fillColor: "#1e3241",
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 1.5,
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 24),
        labelOrigin: new window.google.maps.Point(12, 9),
      };

      const visibleMap = locationMarkersVisibleRef.current ? map : null;

      if (newStartLocation && newStartLocation.lat && newStartLocation.lng) {
        startMarkerRef.current = new window.google.maps.Marker({
          position: newStartLocation,
          map: visibleMap,
          title: "A - Start Location",
          label: pinLabel("A"),
          icon: pinIcon,
        });
      }

      if (newEndLocation && newEndLocation.lat && newEndLocation.lng) {
        endMarkerRef.current = new window.google.maps.Marker({
          position: newEndLocation,
          map: visibleMap,
          title: "B - Destination",
          label: pinLabel("B"),
          icon: pinIcon,
        });
      }

      // If we have both locations, fit the map bounds
      if (newStartLocation && newEndLocation) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(newStartLocation);
        bounds.extend(newEndLocation);
        map.fitBounds(bounds);
      }
    },
    [map],
  );

  const showLocationMarkers = useCallback(() => {
    locationMarkersVisibleRef.current = true;
    if (startMarkerRef.current) startMarkerRef.current.setMap(map);
    if (endMarkerRef.current) endMarkerRef.current.setMap(map);
  }, [map]);

  const hideLocationMarkers = useCallback(() => {
    locationMarkersVisibleRef.current = false;
    if (startMarkerRef.current) startMarkerRef.current.setMap(null);
    if (endMarkerRef.current) endMarkerRef.current.setMap(null);
  }, []);

  /**
   * Remove a waypoint from the route
   */
  const removeWaypoint = useCallback(
    (index) => {
      if (index < 0 || index >= waypointMarkersRef.current.length) return;

      // Remove the marker from the map
      const markerToRemove = waypointMarkersRef.current[index];
      markerToRemove.setMap(null);

      // Update waypoints state and markers ref
      setWaypoints((prev) => prev.filter((_, i) => i !== index));
      waypointMarkersRef.current = waypointMarkersRef.current.filter(
        (_, i) => i !== index,
      );

      // Update the labels on remaining waypoint markers
      waypointMarkersRef.current.forEach((marker, i) => {
        marker.setLabel(`${i + 1}`);
        marker.setTitle(`Waypoint ${i + 1}`);
      });

      // Recalculate route after removing waypoint
      setTimeout(() => calculateRoute(), 100);
    },
    [calculateRoute],
  );

  /**
   * Add a waypoint to the route
   */
  const addWaypoint = useCallback(
    (location) => {
      const newWaypoint = {
        location,
        stopover: true,
      };

      setWaypoints((prev) => [...prev, newWaypoint]);

      // Add marker for the waypoint
      if (map) {
        const waypointMarker = new window.google.maps.Marker({
          position: location,
          map: map,
          title: `Waypoint ${waypoints.length + 1}`,
          label: `${waypoints.length + 1}`,
          draggable: true,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FF5722",
            fillOpacity: 0.8,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
          },
        });

        // Add drag event to update waypoint location
        waypointMarker.addListener("dragend", () => {
          const newPosition = waypointMarker.getPosition();
          const updatedLocation = {
            lat: newPosition.lat(),
            lng: newPosition.lng(),
          };

          // Update the waypoint in state
          setWaypoints((prev) => {
            const index = waypointMarkersRef.current.indexOf(waypointMarker);
            if (index !== -1) {
              const updatedWaypoints = [...prev];
              updatedWaypoints[index] = {
                ...updatedWaypoints[index],
                location: updatedLocation,
              };

              // Recalculate route after waypoint is moved
              setTimeout(() => calculateRoute(), 100);

              return updatedWaypoints;
            }
            return prev;
          });
        });

        // Add click listener to remove waypoint
        waypointMarker.addListener("click", () => {
          const index = waypointMarkersRef.current.indexOf(waypointMarker);
          removeWaypoint(index);
        });

        // Add right-click listener to remove waypoint
        waypointMarker.addListener("rightclick", () => {
          const index = waypointMarkersRef.current.indexOf(waypointMarker);
          removeWaypoint(index);
        });

        // Add mouseover/mouseout for visual feedback
        waypointMarker.addListener("mouseover", () => {
          waypointMarker.setIcon({
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#FF5722",
            fillOpacity: 1.0,
            strokeColor: "#FFFFFF",
            strokeWeight: 3,
          });
        });

        waypointMarker.addListener("mouseout", () => {
          waypointMarker.setIcon({
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#FF5722",
            fillOpacity: 0.8,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
          });
        });

        waypointMarkersRef.current.push(waypointMarker);

        // Recalculate route after adding waypoint
        setTimeout(() => calculateRoute(), 100);
      }
    },
    [map, waypoints.length, calculateRoute, removeWaypoint],
  );

  /**
   * Clear all waypoints
   */
  const clearWaypoints = useCallback(() => {
    // Remove all waypoint markers from the map
    waypointMarkersRef.current.forEach((marker) => marker.setMap(null));
    waypointMarkersRef.current = [];
    setWaypoints([]);

    // Recalculate route without waypoints
    setTimeout(() => calculateRoute(), 100);
  }, [calculateRoute]);

  /**
   * Check if a point is near the route path (for adding waypoints)
   */
  const isPointNearPath = useCallback((point, path, threshold = 0.0005) => {
    if (!path || path.length < 2) return false;

    // Check distance from point to each segment of the path
    for (let i = 0; i < path.length - 1; i++) {
      const start = path[i];
      const end = path[i + 1];

      // Calculate distance from point to line segment
      const distance = distToSegment(
        point.lat,
        point.lng,
        start.lat,
        start.lng,
        end.lat,
        end.lng,
      );

      if (distance < threshold) return true;
    }

    return false;
  }, []);

  /**
   * Calculate distance from point to line segment
   */
  const distToSegment = (x, y, x1, y1, x2, y2) => {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;

    if (len_sq !== 0) param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = x - xx;
    const dy = y - yy;

    return Math.sqrt(dx * dx + dy * dy);
  };

  // Remove the map click listener setup - we'll handle clicks on the polyline directly
  useEffect(() => {
    // This effect is now empty as we handle clicks on the polyline itself
    // Keep the effect for potential future use
  }, [map, isDraggable, activeRoute, addWaypoint, isPointNearPath]);

  // Effect to update markers when locations change
  useEffect(() => {
    if (map && (startLocation || endLocation)) {
      setLocationMarkers(startLocation, endLocation);
    }
  }, [map, startLocation, endLocation, setLocationMarkers]);

  // Keep location refs in sync for use in autocomplete closures
  useEffect(() => {
    startLocationRef.current = startLocation;
  }, [startLocation]);
  useEffect(() => {
    endLocationRef.current = endLocation;
  }, [endLocation]);

  // Auto-calculate route when both locations are set
  useEffect(() => {
    if (skipAutoCalculateRef.current) {
      skipAutoCalculateRef.current = false;
      return;
    }
    if (startLocation && endLocation) {
      calculateRoute();
    }
  }, [startLocation, endLocation, calculateRoute]);

  // Re-draw the polyline when isDraggable changes so click listeners are attached/removed
  useEffect(() => {
    if (activeRoute && activeRoute.length > 0) {
      displayRoute(activeRoute);
    }
  }, [isDraggable, activeRoute, displayRoute]);

  // Show/hide waypoint markers based on customization mode
  useEffect(() => {
    if (isDraggable) {
      // Markers will be re-created by displayRoute above if needed
      waypointMarkersRef.current.forEach((marker) => marker.setMap(map));
    } else {
      // Hide markers so they don't obscure traffic, incidents, and roadwork
      waypointMarkersRef.current.forEach((marker) => marker.setMap(null));
    }
  }, [isDraggable, map]);

  /**
   * Load a saved route by setting start/end locations, text, and intermediates,
   * then calculating the route. Waypoints are [{lat, lng, label}, ...].
   */
  const loadSavedRoute = useCallback(
    async (savedWaypoints) => {
      if (!savedWaypoints || savedWaypoints.length < 2) return;

      const first = savedWaypoints[0];
      const last = savedWaypoints[savedWaypoints.length - 1];
      const intermediates = savedWaypoints.slice(1, -1);

      // Set start
      setStartLocation({ lat: first.lat, lng: first.lng });
      setStart(
        first.label || `${first.lat.toFixed(5)}, ${first.lng.toFixed(5)}`,
      );
      if (startInputRef.current) {
        startInputRef.current.value =
          first.label || `${first.lat.toFixed(5)}, ${first.lng.toFixed(5)}`;
      }

      // Set end
      setEndLocation({ lat: last.lat, lng: last.lng });
      setEnd(last.label || `${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`);
      if (endInputRef.current) {
        endInputRef.current.value =
          last.label || `${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`;
      }

      // Clear existing waypoints
      waypointMarkersRef.current.forEach((marker) => marker.setMap(null));
      waypointMarkersRef.current = [];

      // Set intermediate waypoints
      const newWaypoints = intermediates.map((wp) => ({
        location: { lat: wp.lat, lng: wp.lng },
        stopover: true,
      }));
      setWaypoints(newWaypoints);

      // Calculate the route after a short delay to let state settle
      await new Promise((r) => setTimeout(r, 100));
      return calculateRoute();
    },
    [
      calculateRoute,
      setStart,
      setEnd,
      setStartLocation,
      setEndLocation,
      setWaypoints,
    ],
  );

  /**
   * Load a saved route from the server by ID — uses stored path (no re-planning),
   * then fetches live traffic separately.
   */
  const loadSavedRouteFromServer = useCallback(
    async (routeId, savedWaypoints) => {
      if (!savedWaypoints || savedWaypoints.length < 2) return;

      const first = savedWaypoints[0];
      const last = savedWaypoints[savedWaypoints.length - 1];
      const intermediates = savedWaypoints.slice(1, -1);

      // Suppress the auto-calculate useEffect — we fetch the stored route, not re-plan
      skipAutoCalculateRef.current = true;

      // Set start
      setStartLocation({ lat: first.lat, lng: first.lng });
      setStart(
        first.label || `${first.lat.toFixed(5)}, ${first.lng.toFixed(5)}`,
      );
      if (startInputRef.current) {
        startInputRef.current.value =
          first.label || `${first.lat.toFixed(5)}, ${first.lng.toFixed(5)}`;
      }

      // Set end
      setEndLocation({ lat: last.lat, lng: last.lng });
      setEnd(last.label || `${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`);
      if (endInputRef.current) {
        endInputRef.current.value =
          last.label || `${last.lat.toFixed(5)}, ${last.lng.toFixed(5)}`;
      }

      // Clear existing waypoints
      waypointMarkersRef.current.forEach((marker) => marker.setMap(null));
      waypointMarkersRef.current = [];

      // Set intermediate waypoints
      const newWaypoints = intermediates.map((wp) => ({
        location: { lat: wp.lat, lng: wp.lng },
        stopover: true,
      }));
      setWaypoints(newWaypoints);

      setIsCalculating(true);

      try {
        // Step 1: Get stored route map (no re-planning)
        const mapResult = await getRouteMap(routeId);
        const mapData = mapResult?.data;
        if (!mapData || !mapData.path || mapData.path.length === 0) {
          console.error("No path data from routeMap:", mapResult);
          setIsCalculating(false);
          return;
        }

        const path = mapData.path;

        // Display path immediately
        displayRoute(path);
        setActiveRoute(path);

        const routesData = createRouteObjects(path, mapData);
        setRoutes(routesData);
        setSelectedRouteIndex(0);

        // Step 2: Fetch live traffic + weather in parallel
        const [trafficResult, weatherResult] = await Promise.allSettled([
          getRouteTraffic(routeId),
          getRouteWeather(routeId),
        ]);

        let merged = { ...mapData };

        if (trafficResult.status === "fulfilled") {
          const trafficData = trafficResult.value?.data;
          merged = addAverageSpeed({ ...merged, ...trafficData });
          setTrafficLinks(trafficData?.links || []);
        } else {
          console.warn(
            "Traffic fetch failed for saved route:",
            trafficResult.reason,
          );
          setTrafficLinks([]);
        }

        if (weatherResult.status === "fulfilled") {
          const weatherData = weatherResult.value?.data;
          merged.weatherAlerts = weatherData?.weatherAlerts || [];
          merged.winterConditions = weatherData?.winterConditions || [];
        } else {
          console.warn(
            "Weather fetch failed for saved route:",
            weatherResult.reason,
          );
        }

        setLastRouteResponse(merged);
        const updatedRoutes = createRouteObjects(path, merged);
        setRoutes(updatedRoutes);

        routeChangedListenersRef.current.forEach((listener) => {
          if (typeof listener === "function") listener();
        });

        return mapData.routeData;
      } catch (error) {
        console.error("Error loading saved route from server:", error);
        setTrafficLinks([]);
        return null;
      } finally {
        setIsCalculating(false);
      }
    },
    [
      setStart,
      setEnd,
      setStartLocation,
      setEndLocation,
      setWaypoints,
      createRouteObjects,
    ],
  );

  /**
   * Refresh live traffic data for a saved route without re-planning.
   * Updates lastRouteResponse (preserving routeData) and trafficLinks.
   */
  const refreshTraffic = useCallback(async (routeId) => {
    const [trafficResult, weatherResult] = await Promise.allSettled([
      getRouteTraffic(routeId),
      getRouteWeather(routeId),
    ]);

    if (trafficResult.status === "fulfilled") {
      const trafficData = trafficResult.value?.data;
      if (trafficData) {
        setLastRouteResponse((prev) =>
          addAverageSpeed(prev ? { ...prev, ...trafficData } : trafficData),
        );
        setTrafficLinks(trafficData.links || []);
      }
    } else {
      console.warn("Traffic refresh failed:", trafficResult.reason);
    }

    if (weatherResult.status === "fulfilled") {
      const weatherData = weatherResult.value?.data;
      if (weatherData) {
        setLastRouteResponse((prev) =>
          prev
            ? {
                ...prev,
                weatherAlerts: weatherData.weatherAlerts || [],
                winterConditions: weatherData.winterConditions || [],
              }
            : prev,
        );
      }
    } else {
      console.warn("Weather refresh failed:", weatherResult.reason);
    }
  }, []);

  /**
   * Clear all event markers from the map
   */
  const clearEventMarkers = useCallback(() => {
    eventMarkersRef.current.forEach((marker) => marker.setMap(null));
    eventMarkersRef.current = [];
    if (eventInfoWindowRef.current) {
      eventInfoWindowRef.current.close();
    }
  }, []);

  /**
   * Build popup HTML row for InfoWindow.
   */
  const popupRow = (label, value) => {
    if (!value) return "";
    return `<tr><td style="font-weight:600;padding:2px 8px 2px 0;vertical-align:top;white-space:nowrap;">${label}</td><td style="padding:2px 0;">${value}</td></tr>`;
  };

  /**
   * Build incident popup HTML matching react-ui's Incident popup.
   */
  const buildIncidentPopupHtml = (popup) => {
    const rows =
      popupRow("Location", popup.locDesc) +
      popupRow("Description", popup.desc) +
      popupRow("Closure", popup.closure) +
      popupRow("Start", popup.start) +
      popupRow("End", popup.end) +
      popupRow("Source", popup.src);
    return (
      `<div style="max-width:320px;font-size:13px;color:#1f2937;">` +
      `<table style="border-collapse:collapse;">${rows}</table>` +
      `</div>`
    );
  };

  /**
   * Build construction popup HTML matching react-ui's Construction popup.
   */
  const buildConstructionPopupHtml = (popup) => {
    const rows =
      popupRow("Location", popup.locDesc) +
      popupRow("Description", popup.desc) +
      popupRow("Severity", popup.sev) +
      popupRow("Closure", popup.closure) +
      popupRow("Time", popup.time) +
      popupRow("Duration", popup.dur) +
      popupRow("Source", popup.src) +
      popupRow("Updated", popup.lstUpd);
    return (
      `<div style="max-width:320px;font-size:13px;color:#1f2937;">` +
      `<table style="border-collapse:collapse;">${rows}</table>` +
      `</div>`
    );
  };

  /**
   * Show incident and road work markers on the map
   */
  const showEventMarkers = useCallback(
    (incidents = [], roadWork = []) => {
      clearEventMarkers();
      if (!map) return;

      const infoWindow =
        eventInfoWindowRef.current || new window.google.maps.InfoWindow();
      eventInfoWindowRef.current = infoWindow;

      const createMarker = (lat, lng, iconUrl, title, popupHtml) => {
        if (!lat || !lng) return;
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          title,
          icon: {
            url: iconUrl,
            scaledSize: new window.google.maps.Size(21, 21),
          },
        });
        marker.addListener("click", () => {
          infoWindow.setContent(popupHtml);
          infoWindow.open(map, marker);
        });
        eventMarkersRef.current.push(marker);
        return marker;
      };

      incidents.forEach((inc) => {
        const p = inc.popup;
        const iconUrl = p?.ev
          ? "/mapicons/Incidents/Incident_v14.png"
          : p?.lanes === "full"
            ? "/mapicons/Incidents/Incident_v14.png"
            : "/mapicons/Incidents/Minor_Incident_v14.png";
        const title =
          p?.locDesc ||
          inc.locationDescription ||
          inc.description ||
          "Incident";
        const html = p
          ? buildIncidentPopupHtml(p)
          : `<div style="max-width:250px;font-size:13px;color:#1f2937;"><strong>${title}</strong></div>`;
        const m = createMarker(inc.lat, inc.lng, iconUrl, title, html);
        if (m) m._layerType = "incidents";
      });

      roadWork.forEach((work) => {
        const p = work.popup;
        const title =
          p?.locDesc ||
          work.locationDescription ||
          work.description ||
          "Road work";
        const html = p
          ? buildConstructionPopupHtml(p)
          : `<div style="max-width:250px;font-size:13px;color:#1f2937;"><strong>${title}</strong></div>`;
        const m = createMarker(
          work.lat,
          work.lng,
          "/mapicons/Construction/Construction_A_v9.png",
          title,
          html,
        );
        if (m) m._layerType = "roadWork";
      });
    },
    [map, clearEventMarkers],
  );

  /**
   * Clear all camera markers from the map.
   */
  const clearCameraMarkers = useCallback(() => {
    cameraMarkersRef.current.forEach((marker) => marker.setMap(null));
    cameraMarkersRef.current = [];
    if (cameraInfoWindowRef.current) {
      cameraInfoWindowRef.current.close();
    }
  }, []);

  /**
   * Show camera markers on the map from GeoJSON features.
   */
  const showCameraMarkers = useCallback(
    (features = []) => {
      clearCameraMarkers();
      if (!map || !features.length) return;

      const infoWindow =
        cameraInfoWindowRef.current || new window.google.maps.InfoWindow();
      cameraInfoWindowRef.current = infoWindow;

      features.forEach((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties;
        if (!lat || !lng || props.dis) return;

        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          title: props.locDesc || "Camera",
          icon: {
            url: "/mapicons/Camera/Camera_A_v9.png",
            scaledSize: new window.google.maps.Size(21, 21),
          },
        });
        marker._layerType = "cameras";
        marker.addListener("click", () => {
          infoWindow.setContent(buildCameraPopupHtml(props));
          infoWindow.open(map, marker);
        });
        cameraMarkersRef.current.push(marker);
      });
    },
    [map, clearCameraMarkers],
  );

  /**
   * Set visibility of a specific marker layer type.
   */
  const setLayerVisibility = useCallback((layerType, visible) => {
    const refs =
      layerType === "cameras"
        ? cameraMarkersRef.current
        : eventMarkersRef.current;
    refs.forEach((marker) => {
      if (marker._layerType === layerType) {
        marker.setVisible(visible);
      }
    });
  }, []);

  /**
   * Create a Google Maps custom control for toggling map layers.
   * Returns the control DOM element.
   */
  const createLayerControl = useCallback(
    (mapInstance, initialLayers, onToggle) => {
      const controlDiv = document.createElement("div");
      controlDiv.style.margin = "10px";

      // Toggle button
      const toggleBtn = document.createElement("button");
      toggleBtn.type = "button";
      toggleBtn.title = "Map Layers";
      toggleBtn.style.cssText =
        "background:#fff;border:0;border-radius:2px;box-shadow:rgba(0,0,0,0.3) 0px 1px 4px -1px;" +
        "cursor:pointer;height:40px;width:40px;display:flex;align-items:center;justify-content:center;";
      toggleBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polygon points="12 2 2 7 12 12 22 7 12 2"/>' +
        '<polyline points="2 17 12 22 22 17"/>' +
        '<polyline points="2 12 12 17 22 12"/>' +
        "</svg>";
      controlDiv.appendChild(toggleBtn);

      // Dropdown panel
      const panel = document.createElement("div");
      panel.style.cssText =
        "display:none;background:#fff;border-radius:2px;box-shadow:rgba(0,0,0,0.3) 0px 1px 4px -1px;" +
        "margin-top:4px;padding:8px 0;min-width:160px;";
      controlDiv.appendChild(panel);

      const LAYERS = [
        {
          id: "cameras",
          label: "Cameras",
          icon: "/mapicons/Camera/Camera_A_v9.png",
        },
        {
          id: "roadWork",
          label: "Road Work",
          icon: "/mapicons/Construction/Construction_A_v9.png",
        },
        {
          id: "incidents",
          label: "Incidents",
          icon: "/mapicons/Incidents/Incident_v14.png",
        },
      ];

      // Track current state
      const state = { ...initialLayers };

      LAYERS.forEach((layer) => {
        const row = document.createElement("div");
        row.style.cssText =
          "display:flex;align-items:center;gap:8px;padding:6px 12px;cursor:pointer;user-select:none;font-family:Roboto,Arial,sans-serif;font-size:13px;color:#333;";
        row.addEventListener("mouseenter", () => {
          row.style.backgroundColor = "#f3f4f6";
        });
        row.addEventListener("mouseleave", () => {
          row.style.backgroundColor = "";
        });

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.checked = state[layer.id] !== false;
        checkbox.style.cssText =
          "cursor:pointer;width:16px;height:16px;accent-color:#4285F4;";

        const icon = document.createElement("img");
        icon.src = layer.icon;
        icon.style.cssText = "width:18px;height:18px;";

        const label = document.createElement("span");
        label.textContent = layer.label;

        row.appendChild(checkbox);
        row.appendChild(icon);
        row.appendChild(label);
        panel.appendChild(row);

        const handleClick = (e) => {
          if (e.target !== checkbox) {
            checkbox.checked = !checkbox.checked;
          }
          state[layer.id] = checkbox.checked;
          onToggle(layer.id);
        };
        row.addEventListener("click", handleClick);
      });

      // Toggle panel visibility
      let panelOpen = false;
      toggleBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        panelOpen = !panelOpen;
        panel.style.display = panelOpen ? "block" : "none";
      });

      // Close panel when clicking elsewhere on the map
      mapInstance.addListener("click", () => {
        panelOpen = false;
        panel.style.display = "none";
      });

      return controlDiv;
    },
    [],
  );

  /**
   * Clear all route state and map elements. Used when starting a fresh route
   * (e.g., clicking "+" in My Routes) so stale locations don't trigger an
   * unintended route calculation.
   */
  const clearRoute = useCallback(() => {
    setStartLocation(null);
    setEndLocation(null);
    setLastRouteResponse(null);
    setTrafficLinks([]);
    setRoutes([]);
    setActiveRoute(null);
    setWaypoints([]);

    // Clear map elements
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (startMarkerRef.current) {
      startMarkerRef.current.setMap(null);
      startMarkerRef.current = null;
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.setMap(null);
      endMarkerRef.current = null;
    }
    waypointMarkersRef.current.forEach((m) => m.setMap(null));
    waypointMarkersRef.current = [];
    editPointMarkersRef.current.forEach((m) => m.setMap(null));
    editPointMarkersRef.current = [];
  }, []);

  return {
    map,
    directionsService: {
      route: (request, callback) => {
        // This is a mock of the Google DirectionsService
        // When this is called, we'll use our custom route calculation
        calculateRoute()
          .then((routesData) => {
            if (routesData) {
              callback(
                {
                  routes: routesData,
                  status: "OK",
                },
                "OK",
              );
            } else {
              callback(null, "ZERO_RESULTS");
            }
          })
          .catch((error) => {
            console.error("Error in directionsService.route:", error);
            callback(null, "ERROR");
          });
      },
    },
    directionsRenderer: directionsRendererRef.current,
    mapRef,
    startInputRef,
    endInputRef,
    startAutocompleteRef,
    endAutocompleteRef,
    isDraggable,
    setIsDraggable,
    handleRouteChanged,
    startLocation,
    endLocation,
    start,
    setStart,
    end,
    setEnd,
    waypoints,
    addWaypoint,
    removeWaypoint,
    clearWaypoints,
    clearRoute,
    activeRoute,
    captureActiveRoutePath,
    getCurrentLocation,
    isLocating,
    isCalculating,
    routeError,
    calculateRoute,
    loadSavedRoute,
    loadSavedRouteFromServer,
    refreshTraffic,
    lastRouteResponse,
    trafficLinks,
    showEventMarkers,
    clearEventMarkers,
    showCameraMarkers,
    clearCameraMarkers,
    setLayerVisibility,
    createLayerControl,
    showLocationMarkers,
    hideLocationMarkers,
  };
};

export default useCustomRouting;
